import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Image } from 'foundations-components/transitional/components/Image';
import { Lightbox } from 'foundations-components/transitional/components/Lightbox';
import { Section } from 'foundations-components/transitional/components/Section';
import { useFocusCloseButton } from 'marketplace-ui-common/hooks/useFocusCloseButton';
import { Fragment, useMemo, useState } from 'react';
import { DetailsPageCard } from './DetailsPageCard';
import { FeatureCompatibilityMessage } from '../../compatibility/FeatureCompatibilityMessage';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Feature({
  children,
  feature,
  onImageClick
}) {
  const [showLightBox, setShowLightBox] = useState(false);
  useFocusCloseButton(showLightBox);
  const {
    title,
    description,
    imageUrl
  } = feature;
  const renderedLightbox = useMemo(() => showLightBox && feature.imageUrl && /*#__PURE__*/_jsx("div", {
    "data-test-id": "feature-lightbox",
    children: /*#__PURE__*/_jsx(Lightbox, {
      imageAlt: feature.description,
      imageSrc: feature.imageUrl,
      onClose: () => setShowLightBox(false),
      children: /*#__PURE__*/_jsx(ButtonWrapper, {
        children: feature.description
      })
    })
  }), [feature, showLightBox, setShowLightBox]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsxs(Grid, {
        children: [/*#__PURE__*/_jsx(GridItem, {
          size: {
            sm: 12,
            md: 5
          },
          children: imageUrl && /*#__PURE__*/_jsx(Clickable, {
            onClick: () => {
              setShowLightBox(true);
              if (onImageClick) {
                onImageClick();
              }
            },
            children: /*#__PURE__*/_jsx(Image, {
              alt: title,
              src: imageUrl,
              responsive: true,
              shape: "rounded"
            })
          })
        }), /*#__PURE__*/_jsxs(GridItem, {
          size: {
            sm: 12,
            md: 7
          },
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(BreakString, {
              children: title
            })
          }), /*#__PURE__*/_jsx(Text, {
            whiteSpace: "pre-line",
            children: /*#__PURE__*/_jsx(BreakString, {
              children: description
            })
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "200",
            children: children
          })]
        })]
      })
    }, `feature-${title}`), renderedLightbox]
  });
}
export function ProductFeaturesCard({
  features,
  integrationFeatures,
  missingCompatibilityRequirements,
  onImageClick,
  onCompatibilityModalOpen
}) {
  const integrationFeaturesAsFeatures = integrationFeatures.map(integrationFeature => ({
    // put the deprecated integrationFeature
    // into the shape of a FeatureResponse
    type: 'MARKETPLACE_LISTING',
    title: integrationFeature.headline,
    scopeGroups: [],
    description: integrationFeature.text,
    imageUrl: integrationFeature.imageUrl,
    illustrationKey: '',
    guides: [],
    internal: {
      scopes: []
    }
  }));
  const renderedFeatures = useMemo(() => {
    if (features !== null && features !== void 0 && features.length) {
      return features.map((feature, index) => {
        const missingCompatibilityRequirement = missingCompatibilityRequirements && missingCompatibilityRequirements[index];
        return /*#__PURE__*/_jsx(Feature, {
          feature: feature,
          onImageClick: onImageClick,
          children: /*#__PURE__*/_jsx(FeatureCompatibilityMessage, {
            missingCompatibilityRequirement: missingCompatibilityRequirement,
            onCompatibilityModalOpen: onCompatibilityModalOpen
          })
        }, feature.title);
      });
    } else {
      return integrationFeaturesAsFeatures.map(feature => /*#__PURE__*/_jsx(Feature, {
        feature: feature,
        onImageClick: onImageClick
      }, feature.title));
    }
  }, [features, integrationFeaturesAsFeatures, missingCompatibilityRequirements, onImageClick, onCompatibilityModalOpen]);
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.featuresCard.title"
    }),
    actions: features !== null && features !== void 0 && features.length ? /*#__PURE__*/_jsx(Button, {
      use: "link",
      onClick: onCompatibilityModalOpen,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.featuresCard.compare"
      })
    }) : undefined,
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      children: renderedFeatures.length > 2 ? /*#__PURE__*/_jsx("div", {
        "data-test-id": "expandable-features",
        children: /*#__PURE__*/_jsx(ExpandableText, {
          maxHeight: 390,
          hideButtonCaret: true,
          buttonAlign: "left",
          children: renderedFeatures
        })
      }) : renderedFeatures
    })
  });
}